import sumBy from 'lodash/sumBy'
import { mapGetters, mapActions } from 'vuex'
import { getStyleOrder, getCardIcons, getSubsettedCardIcons, doesSubsettedKitIncludeFamilyStyle } from 'app/releases'

const KITS_INFINITY = -1
const PAGEVIEWS_INFINITY = -1

export const KitSubscriptionMixin = {
  data() {
    return {
      // TODO: Metered Billing full release: remove
      isMeteredBillingPageviewsPartiallyReleased: true
    }
  },

  computed: {
    canCreateProKits() {
      return this.$permits.get('accessToKits', 'proAllowed')
    },

    hasInfiniteKits() {
      return this.$permits.get('accessToKits', 'kitLimit') === KITS_INFINITY
    },

    hasPageviewsUnlimited() {
      return this.meteredBilling?.maxMonthlyPageviews === PAGEVIEWS_INFINITY
    },

    hasSetPageviewsBillingLimit() {
      return this.$subscription?.meteredBilling.maxMonthlyPageviewsMoneyLimit > 0
    },

    isMeteredBillingPageviews() {
      return this.isMeteredBillingPageviewsFeatureOn && this.isMeteredBillingPageviewsCustomerEnabled
    },

    isMeteredBillingPageviewsCustomerEnabled() {
      return this.meteredBilling?.meteredPageviewsOn
    },

    isMeteredBillingPageviewsFeatureOn() {
      return FONTAWESOME.METERED_PAGEVIEWS_ACTIVE === 'yes'
    },

    kitLimit() {
      return this.$permits.get('accessToKits', 'kitLimit') || 0
    },

    kitPageviewsOverageLimit() {
      return this.isMeteredBillingPageviewsCustomerEnabled ? this.totalPageviewsUsageLimit - this.pageviewsPlanLimit : 0
    },

    pageviewsDollarLimit() {
      return this.meteredBilling?.maxMonthlyPageviewMoneyLimit > 0 ? this.meteredBilling?.maxMonthlyPageviewsMoneyLimit / 100 : 0
    },

    pageviewsPlanLimit() {
      return this.$subscription?.plan?.monthlyPageviews || 0
    },

    planHasAccessToPageviewMeteredBilling() {
      return this.$subscription?.plan?.meteredBillingPageviewsEnabled
    },

    overagePageviewIncrement() {
      return this.$subscription?.plan?.overagePageviewIncrement || 0
    },

    totalPageviewsUsageLimit() {
      return this.meteredBilling?.maxMonthlyPageviews
    }
  }
}

export const KitsStatusAndUsageMixin = {
  data() {
    return {
      hasFetchedKits: false,
      kitsFetchFailed: false
    }
  },

  computed: {
    ...mapGetters('kits', ['currentKits', 'pendingKits', 'activeKits']),

    ...mapGetters('meteredBilling', ['meteredBilling']),

    ...KitSubscriptionMixin.computed,

    kitCount() {
      // pending kits don't count towards the kit count
      return this.activeKits.length
    },

    doNotAllowAnyMoreKits() {
      if (this.hasPendingKit) {
        return true
      } else {
        return !this.hasInfiniteKits && this.kitCount >= this.kitLimit
      }
    },

    hasPendingKit() {
      return this.pendingKits.length > 0
    },

    kitsLoading() {
      return !this.hasFetchedKits
    },

    atPageviewsPlanLimit() {
      return this.totalPageviews >= this.pageviewsPlanLimit
    },

    totalPageviews() {
      return sumBy(this.currentKits, function (k) {
        return k.pageviews
      })
    },

    hasReachedPageviewsOverageLimit() {
      return this.isMeteredBillingPageviews && !this.hasPageviewsUnlimited && this.totalPageviews > this.totalPageviewsUsageLimit
    },

    kitUsagePercentage() {
      return (this.totalPageviews / this.pageviewsPlanLimit) * 100
    },

    kitPageviewsOverageUsage() {
      const overage = this.totalPageviews - (this.$subscription?.plan?.monthlyPageviews || 0)

      return Math.max(overage, 0)
    },

    kitPageviewsOverageUsagePercentage() {
      return (this.kitPageviewsOverageUsage / this.$subscription?.plan?.monthlyPageviews) * 100
    },

    kitPageviewsOverageDangerPercentage() {
      return this.kitPageviewsOverageLimit ? 100 : this.$subscription?.plan?.monthlyPageviews * 10
    },

    kitsDeactivated() {
      return this.currentKits.some((k) => k.status === 'deactivated')
    },

    billablePageviewsOverage() {
      const planPageviewsAllowance = this.$subscription?.plan?.monthlyPageviews || 0

      const actualOveragePageviews = Math.max(this.totalPageviews - planPageviewsAllowance, 0)

      const overagePageviewIncrement = this.$subscription?.plan?.overagePageviewIncrement || 0

      const isOverageLessThanOneIncrement = actualOveragePageviews < overagePageviewIncrement

      if (isOverageLessThanOneIncrement) {
        // We don't bill for these small overages.
        return 0
      }

      if (this.isMeteredBillingPageviewsCustomerEnabled && this.hasPageviewsUnlimited) {
        return actualOveragePageviews
      }

      const isMeteredBillingLimitedWithExcess =
        this.isMeteredBillingPageviewsCustomerEnabled && this.totalPageviewsUsed >= this.meteredBilling?.maxMonthlyPageviews

      if (isMeteredBillingLimitedWithExcess) {
        // If metered billing is enabled, with a limit,
        // we cap billable overages at the max monthly limit set by the customer.
        return Math.max(this.meteredBilling?.maxMonthlyPageviews - planPageviewsAllowance, 0)
      }

      return actualOveragePageviews
    }
  },

  methods: {
    ...mapActions('kits', { fetchKitsFromStore: 'fetchKits' }),

    async fetchKits() {
      if (this.$account) {
        try {
          await this.fetchKitsFromStore()

          this.hasFetchedKits = true
        } catch (e) {
          this.kitsFetchFailed = true
        }
      }
    }
  }
}

export const DomainsMixin = {
  methods: {
    hasDomains(kit) {
      return kit.domains.indexOf('*.*') === -1
    },

    numberOfDomainsLabel(kit) {
      const num = kit.domains.length

      return `${num} ${num === 1 ? 'domain' : 'domains'}`
    }
  }
}

export const KitIconCollectionMixin = {
  computed: {
    ...mapGetters('iconCollections', ['currentIconCollections']),

    proIconCollection() {
      const proIconCollection = this.currentIconCollections.find((collection) => collection.name === 'pro-collection')

      proIconCollection.families = proIconCollection.familyStyles.reduce((acc, fs) => {
        return this.addOrUpdateFamily(acc, fs.family, fs.style)
      }, [])

      return proIconCollection
    },

    proPlusIconCollection() {
      const proPlusIconCollection = this.currentIconCollections.find((collection) => collection.name === 'pro-plus-collection')

      proPlusIconCollection.families = proPlusIconCollection.familyStyles.reduce((acc, fs) => {
        const isInPro = this.proIconCollection.familyStyles.some((f) => f.family === fs.family && f.style === fs.style)

        if (!isInPro) {
          return this.addOrUpdateFamily(acc, fs.family, fs.style)
        } else {
          return acc
        }
      }, [])

      return proPlusIconCollection
    },

    proFamilyStyles() {
      return this.getFamilyStyleList(this.proIconCollection)
    },

    proPlusFamilyStyles() {
      return this.getFamilyStyleList(this.proPlusIconCollection)
    }
  },

  methods: {
    ...mapActions('iconCollections', ['fetchIconCollections']),

    async loadIconCollections() {
      await this.fetchIconCollections()
    },

    addOrUpdateFamily(families, family, style) {
      const familyObj = families.find(f => f.family === family)
      const styleObj = {
        style: style,
        family: family,
        selected: false
      }

      if (familyObj) {
        familyObj.styles.push(styleObj)
      } else {
        families.push({
          family: family,
          styles: [styleObj]
        })
      }

      return families
    },

    setSelectedStyles(kit) {
      // Call this method to set the selected styles for the kit
      this.selectStyles(kit, this.proIconCollection)
      this.selectStyles(kit, this.proPlusIconCollection)
    },

    selectStyles(kit, collection) {
      collection.families.forEach((family) => {
        family.styles.forEach((style) => {
          style.selected = this.getSubsetData(kit, style.family, style.style)
        })
      })
    },

    getSubsetData(kit, family, style) {
      if (kit.subset && 'icons' in kit.subset) {
        return kit.subset.icons.find((s) => s.family == family && s.style == style)
      } else {
        return null
      }
    },

    selectAllStyles() {
      this.setAllStylesForCollection(this.proIconCollection)
      this.setAllStylesForCollection(this.proPlusIconCollection)
    },

    setAllStylesForCollection(collection) {
      collection.families.forEach((family) => {
        family.styles.forEach((style) => {
          style.selected = true
        })
      })
    },

    getFamilyStyleList(collection) {
      const hasKit = (this.kit ? true : false)

      return collection.families.reduce((acc, family) => {
        family.styles.forEach((fs) => {
          acc.push({
          family: fs.family,
            style: fs.style,
            cardIcons: this.fetchCardIcons(hasKit, fs.family, fs.style),
            order: getStyleOrder(this.version, fs.family, fs.style),
            selected: fs.selected
          })
        })
        return acc
      }, []).sort((a, b) => (a.order > b.order ? 1 : -1))
    },

    fetchCardIcons(hasKit, family, style) {
      if (hasKit) {
        const isCustomSubsetted = this.kit.subset !== null && this.kit.subset.type === 'custom'
        const includesStyle = doesSubsettedKitIncludeFamilyStyle(this.kit, family, style)
        return isCustomSubsetted && includesStyle ? getSubsettedCardIcons(this.kit, family, style) : getCardIcons(style)
      } else {
        return getCardIcons(style)
      }
    }
  }
}